import { createSelector, createSlice } from "@reduxjs/toolkit";
import { sliceNames } from "../util/constants";
import { formApi } from "./api";
import { storageApi } from "../storage/api";
import { formatFormTypeByConditional, getEasternFormat } from "../../util/functions";
import { selectAuthenticatedUserRoles, selectIsAdmin, selectIsUser, selectLoggedInEmplid } from "../authorization/slices";
import { FORM_STATUSES, ROLES, STATUSES } from "../../util/constants";
import { syncAmplifyReduxUserAttributes } from "../authorization/thunks";
import { selectActiveReportingPeriod } from "../submissionPeriod/slices";
import { ROUTE_KEYS, ROUTES } from "../../_configuration/routing/constants";

const storeFormInformation = (info = {}, item = {}) => {
  const { id, foreignSourceName, type } = item;
  const pathKey = ROUTES[ROUTE_KEYS.FORMS].path || "/forms"
  const path = (type && id) ? `${pathKey}/${type}/${id}` : '';
  let formLabel = foreignSourceName
  if(!formLabel) {
    formLabel = id + (" (No Foreign Source Name Available)")
  }
  info[item.id] = {
    label: formLabel,
    path
  };
  return info
}

const formSlice = createSlice({
  name: sliceNames.form,
  initialState: {
    loading: false,
    error: null,
    status: {
      all: {}
    },
    dataGrid: {
      status: STATUSES.IDLE,
      sortModel: [],
      filterModel: {
        items: []
      },
      ids: [],
      referenceMap: {}
    }
  },
  reducers: {
    storeSubmission: (state, action) => {
      const { formId, submission } = action.payload;
      if (formId && submission) {
        state[formId] = submission;
      }
    },
    setSortModel(state, action) {
      state.dataGrid.sortModel = action.payload;
      state.dataGrid.status = STATUSES.SUCCESS;
    },
    setFilterModel(state, action) {
      state.dataGrid.filterModel = action.payload;
      state.dataGrid.status = STATUSES.SUCCESS;
    },
    setNextIDs(state, action) {
      state.dataGrid.ids = action.payload;
    },
    setReferenceMap(state, action) {
      const nextMap = action.payload || {}
      Object.assign(state.dataGrid.referenceMap, nextMap)
    },
  },
 extraReducers: (builder) => {
    builder.addMatcher(
      formApi.endpoints.getForm.matchPending,
      (state, result) => {
        const { id } = result?.meta?.arg?.originalArgs || {}
        if(id) { 
          state.status[id] = STATUSES.LOADING
        }
      }
    );
    builder.addMatcher(
      formApi.endpoints.getForm.matchFulfilled,
      (state, result) => {
        const { id } = result?.meta?.arg?.originalArgs || {}
        if(id) { 
          state.status[id] = STATUSES.SUCCESS
          const formInfo = result?.payload
          
          if(id === formInfo?.id) {
            storeFormInformation(state.dataGrid.referenceMap, formInfo);
          }
        }
      }
    );
    builder.addMatcher(
      formApi.endpoints.getForm.matchRejected,
      (state, result) => {
        const { id } = result?.meta?.arg?.originalArgs || {}
        if(id) { 
          state.status[id] = STATUSES.ERROR
        }
      }
    );
    builder.addMatcher(
      syncAmplifyReduxUserAttributes.fulfilled.match,
      (state, result) => {
        const authState = {
          authentication: {
            user: result.payload
          }
        }
        const isAdmin = selectIsAdmin(authState)
        const isUser = selectIsUser(authState)
        const emplid = selectLoggedInEmplid(authState)
        const hasFilters = state.dataGrid?.filterModel?.items?.length
        if(!isAdmin && isUser && state.dataGrid.status === STATUSES.IDLE && !hasFilters) {
          state.dataGrid.filterModel.items = [{ field: "emplid", operator: "equals", value: emplid }]
        }
      }
    );
    builder.addMatcher(
      formApi.endpoints.getAllForms.matchFulfilled,
      (state, result) => {
        const forms = result?.payload || []
        if(Array.isArray(forms) && forms?.length) {
          const reduced = forms.reduce(storeFormInformation, {});
          Object.assign(state.dataGrid.referenceMap, reduced);
        }
      }
    );

  }
});

const { storeSubmission, setSortModel, setFilterModel, setNextIDs, setReferenceMap } = formSlice.actions;
const selectFormValues = (state, formType, id) => {
  return formApi.endpoints.getForm.select({ formType, id })(state)?.data;
};

const selectMetadataValues = (state, formType, id) => {
  const {
      foreignSourceName,
      year,
      period,
      isRestrictedOrConditional, 
      status,
      balancingUnit,
      osuUniqueID,
      sourceAmount,
      submittingUnit,
      countryOfAttribution,
      lastModified
    } = selectFormValues(state, formType, id) || {}

    return {
      foreignSourceName,
      year,
      period,
      isRestrictedOrConditional, 
      status,
      balancingUnit,
      osuUniqueID,
      sourceAmount,
      submittingUnit,
      countryOfAttribution,
      lastModified
    }  
};


const selectLoadingStatusForId = (state, id) => {
  return state[sliceNames.form]?.status?.[id]
};


const selectCurrentFormDataStatus = (state, formType, id) => {
  const status = selectFormValues(state, formType, id)?.status
  return status
};

const selectCurrentFormDataS3Path = (state, formType, id) => {
  const s3Path = selectFormValues(state, formType, id)?.s3Path
  return s3Path
};

const selectDescriptionList = createSelector(
  [
    (state, formType, id) => {
      if (!id) return null
      return selectFormValues(state, formType, id)
    },
    (state, formType, id) => formType
  ],
  (formValues, formType) => {
    if (!formValues) return []
    const { year, period, typeFromData, isRestrictedOrConditional, emplid, nameN, lastModified } = formValues
    return [
      { field: "Submitter Name.#", value: nameN },
      { field: "Submitter Emplid", value: emplid },
      { field: "Last Updated", value: getEasternFormat(lastModified)},
      { field: "Year", value: year },
      { field: "Period", value: period },
      { field: "Form Type", value: formatFormTypeByConditional({
        type: typeFromData || formType,
        isRestrictedOrConditional
      })}
    ]
  }
)

const selectDefaultValue = (state, { formId, formType, name }) => {
  if (!formId || !name) return "";
  const formValues = selectFormValues(state, formType, formId);        
  
  return formValues?.[name] || "";
};

const selectFormJSONStatus = (state, s3Path) => {
  const questionsState = storageApi.endpoints.downloadJsonData.select({
    path: s3Path,
  })(state);
  return questionsState?.status;
};

const selectDynamoFormState = ({ type, id }) => {
  return formApi.endpoints.getForm.useQueryState({
    formType: type,
    id,
  })
};

const selectDynamoFormStatus = ({ type, id }) => {
  return selectDynamoFormState({ type, id })?.status
};

const selectDynamoFormIsLoading = ({ type, id }) => {
  return selectDynamoFormState({ type, id })?.isLoading
};



const selectActionsToAllow = (state, { formType, id }) => {
  const formValues = selectFormValues(state, formType, id);
  const { isActive: isPeriodActive, year: expectedYear, period: expectedPeriod } = selectActiveReportingPeriod(state);
  let isActive = isPeriodActive
  const currentEmplid = selectLoggedInEmplid(state);
  const roles = selectAuthenticatedUserRoles(state) || [];
  const isAdmin = roles.includes(ROLES.ADMINS);
  if(id && isPeriodActive) {
    const correctYear = expectedYear === formValues?.year && expectedPeriod === formValues?.period
    const correctUser =  isAdmin || currentEmplid === formValues?.emplid
    isActive = (correctYear && correctUser)
  }
  const formStatus = selectCurrentFormDataStatus(state, formType, id);
  const isUser = roles.includes(ROLES.USERS);
  if (!isActive && isUser && !isAdmin) {
    return {
      formStatus,
      showStandardSubmitButton: false,
      showSaveButton: false,
      showDeleteButton: false,
      showAcceptButton: false,
      actionable: false,
    };
  }
  const isDraft = formStatus === FORM_STATUSES.DRAFT;
  const isSubmitted = formStatus === FORM_STATUSES.SUBMITTED;
  const userOnOwnForm =
    (isAdmin || isUser) &&
    currentEmplid &&
    currentEmplid === formValues?.emplid;
  const creatingNewForm = !id && (isAdmin || isUser);
  const isEditingDraftForm = id && isDraft && (isAdmin || userOnOwnForm);
  const isAdminOnSubmittedForm = id && isAdmin && isSubmitted;
  const showStandardSubmitButton = creatingNewForm || isEditingDraftForm;
  const showSaveButton =
    creatingNewForm || isEditingDraftForm || isAdminOnSubmittedForm;
  const showDeleteButton = isEditingDraftForm || isAdminOnSubmittedForm;
  const showAcceptButton = false;
  const actionable = showAcceptButton ||
    showStandardSubmitButton || showSaveButton || showDeleteButton
  return {
    formStatus,
    showStandardSubmitButton,
    showSaveButton,
    showDeleteButton,
    showAcceptButton,
    actionable
  };
};

  const selectFormIsReadonly = (state, { id, type }) =>{
    const { actionable } = selectActionsToAllow(state, {
      id,
      formType: type,
    });
    const formStatus = selectCurrentFormDataStatus(state, type, id);
    if(!id && actionable) {
      return false
    }
    return !actionable || !(formStatus === FORM_STATUSES.DRAFT || !id)
}

const selectDataGrid = (state) => {
  return state[sliceNames.form]?.dataGrid || {}
}

const selectSortModel = (state) => {
  return selectDataGrid(state)?.sortModel || []
}

const selectFilterModel = createSelector(
  [selectDataGrid],
  (dataGrid) => dataGrid?.filterModel
);

const selectAllNextIds = (state) => {
  return selectDataGrid(state)?.ids || []
}

const selectNextId = (state, currentId) => {
  const identifiers = selectAllNextIds(state)
  const currentIndex = identifiers.indexOf(currentId)
  if(currentIndex === -1) {
    return ""
  }
  return identifiers?.[currentIndex + 1] || ""
}

const selectInformationOnIdentifier = (state, identifier) => {
  let info = {}
  if(identifier) {
    const referenceMap = selectDataGrid(state)?.referenceMap || {}
    if(referenceMap?.[identifier]) {
      info = referenceMap[identifier]
    } 
  }
  return info
}

export {
  storeSubmission,
  selectFormValues,
  selectDefaultValue,
  selectFormJSONStatus,
  selectDynamoFormIsLoading,
  selectDynamoFormStatus,
  selectCurrentFormDataStatus,
  selectDescriptionList,
  selectCurrentFormDataS3Path,
  selectActionsToAllow,
  selectLoadingStatusForId,
  selectFormIsReadonly,
  setSortModel, 
  setFilterModel,
  selectSortModel,
  selectFilterModel,
  selectDataGrid,
  setNextIDs,
  selectNextId,
  selectInformationOnIdentifier,
  setReferenceMap,
  selectMetadataValues
};

export default formSlice;
